import { WORLD_H, WORLD_W } from '@cuboom/shared';

export interface Point {
  x: number;
  y: number;
}

/** 2D camera: world-space center + zoom. All screen coords are CSS pixels. */
export class Camera {
  x = WORLD_W / 2;
  y = WORLD_H / 2;
  zoom = 0.5;
  viewW = 1;
  viewH = 1;

  minZoom = 0.3;
  maxZoom = 2.5;

  setView(w: number, h: number): void {
    this.viewW = Math.max(1, w);
    this.viewH = Math.max(1, h);
  }

  screenToWorld(sx: number, sy: number): Point {
    return {
      x: this.x + (sx - this.viewW / 2) / this.zoom,
      y: this.y + (sy - this.viewH / 2) / this.zoom,
    };
  }

  worldToScreen(wx: number, wy: number): Point {
    return {
      x: (wx - this.x) * this.zoom + this.viewW / 2,
      y: (wy - this.y) * this.zoom + this.viewH / 2,
    };
  }

  /** Move the center by a screen-space delta (drag). */
  panScreen(dx: number, dy: number): void {
    this.x -= dx / this.zoom;
    this.y -= dy / this.zoom;
    this.clamp();
  }

  /** Move the center by a world-space delta. */
  panWorld(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.clamp();
  }

  /** Zoom keeping the world point under (sx, sy) fixed. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const before = this.screenToWorld(sx, sy);
    this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoom * factor));
    const after = this.screenToWorld(sx, sy);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
    this.clamp();
  }

  /** Fit the whole world in view (with margin). */
  fitWorld(margin = 1.04): void {
    // Clamp: called before the first setView the view is still 1×1, which
    // would otherwise compute a microscopic zoom and never recover.
    const z = Math.min(this.viewW / WORLD_W, this.viewH / WORLD_H) * margin;
    this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, z));
    this.x = WORLD_W / 2;
    this.y = WORLD_H / 2;
    this.clamp();
  }

  /** Keep the viewport inside world bounds when the world is larger. */
  clamp(): void {
    const hw = this.viewW / (2 * this.zoom);
    const hh = this.viewH / (2 * this.zoom);
    if (hw * 2 >= WORLD_W) this.x = WORLD_W / 2;
    else this.x = Math.min(WORLD_W - hw, Math.max(hw, this.x));
    if (hh * 2 >= WORLD_H) this.y = WORLD_H / 2;
    else this.y = Math.min(WORLD_H - hh, Math.max(hh, this.y));
  }

  /** Ease toward a target — used for follow-cam. */
  followTo(tx: number, ty: number, t: number): void {
    this.x += (tx - this.x) * t;
    this.y += (ty - this.y) * t;
    this.clamp();
  }
}
